// @flow

/**
 * Merchant id resolution helpers.
 *
 * The disputes module is mounted by the master (publisher account) app which is
 * expected to pass `merchant_id`. When it does not, we fall back to the merchant
 * in the session JWT `redirect_url` instead of firing requests against a broken
 * URL such as `/publisher/null/dictionaries`.
 */

export const MERCHANT_NOT_SET_ERROR = 'app.support.disputes.errors.merchant-not-set';

export class MerchantNotSetError extends Error {
  constructor(message?: string) {
    super(message || MERCHANT_NOT_SET_ERROR);
    this.name = 'MerchantNotSetError';
  }
}

const decodeBase64 = (value: string): ?string => {
  try {
    if (typeof atob !== 'function') {
      return null;
    }

    const binary = atob(value);
    try {
      return decodeURIComponent(escape(binary));
    } catch (error) {
      return binary;
    }
  } catch (error) {
    return null;
  }
};

/**
 * Decode a JWT payload without verifying the signature.
 * Signature verification is the server's responsibility; here we only read the
 * claims to recover the merchant id.
 */
export const decodeJwtPayload = (token: ?string): ?Object => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  const parts = token.split('.');
  if (parts.length < 2 || !parts[1]) {
    return null;
  }

  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const decoded = decodeBase64(padded);
  if (!decoded) {
    return null;
  }

  try {
    const payload = JSON.parse(decoded);
    return payload && typeof payload === 'object' ? payload : null;
  } catch (error) {
    return null;
  }
};

/**
 * Coerce an arbitrary value into a positive integer merchant id.
 * Returns null for anything that cannot be a real merchant id
 * (null, undefined, '', 0, negatives, NaN, floats, non-numeric strings).
 */
export const normalizeMerchantId = (value: mixed): ?number => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const id = Number(value);
  if (!Number.isFinite(id) || !Number.isInteger(id) || id <= 0) {
    return null;
  }

  return id;
};

export const isValidMerchantId = (value: mixed): boolean => normalizeMerchantId(value) !== null;

/**
 * Extract the merchant id from the JWT `redirect_url` claim, which points at the
 * publisher page the user came from: `https://publisher.xsolla.com/{merchantId}/...`.
 */
export const getMerchantIdFromRedirectUrl = (token: ?string): ?number => {
  const payload = decodeJwtPayload(token);
  const redirectUrl = payload && payload.redirect_url;
  if (!redirectUrl || typeof redirectUrl !== 'string') {
    return null;
  }

  try {
    const {pathname} = new URL(redirectUrl, 'http://localhost');
    const match = pathname.match(/^\/(\d+)(?:\/|$)/);
    return match ? normalizeMerchantId(match[1]) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Resolve the merchant id to use, in order of preference:
 * 1. the value passed by the master app;
 * 2. the merchant from the JWT `redirect_url`.
 * `partner_data.merchants[].id` is deliberately not used: it is not a merchant id.
 * Returns null when no source provides a usable id.
 */
export const resolveMerchantId = (merchantId: mixed, token: ?string): ?number => {
  const provided = normalizeMerchantId(merchantId);
  if (provided !== null) {
    return provided;
  }

  return getMerchantIdFromRedirectUrl(token) || null;
};
